import { Directive, ElementRef, HostListener, Input, OnInit, booleanAttribute, inject } from '@angular/core';
import { NgControl } from '@angular/forms';

// Decimal input that accepts both "." and "," as decimal separator (normalized to "."),
// with at most `decimals` decimals. Use on an `<input type="text" inputmode="decimal">` —
// a native number input rejects "," in most locales. The bound control always receives a
// number (or null when empty), never the raw string, so existing calculations keep working.
// With `padDecimals`, the value is always displayed with exactly `decimals` decimals
// (26 -> 26.00), both when written from the model and when the user leaves the field.
@Directive({
  selector: 'input[decimalInput]'
})
export class DecimalInputDirective implements OnInit {
  @Input() decimals = 2;
  @Input({ transform: booleanAttribute }) padDecimals = false;

  private el = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private ngControl = inject(NgControl, { optional: true, self: true });

  // The accessor has to be wrapped here, not in ngOnInit: formControlName/formControl wire
  // the control to it (registerOnChange/writeValue) in their own ngOnChanges, which runs
  // before this directive's ngOnInit, so a later wrap would never be called.
  constructor() {
    const accessor = this.ngControl?.valueAccessor;
    if (!accessor) return;

    const originalWrite = accessor.writeValue.bind(accessor);
    accessor.writeValue = (value: any) => {
      originalWrite(value);
      // Don't reformat under the user's cursor while they're typing.
      if (document.activeElement !== this.el.nativeElement) this.format();
    };

    const originalRegisterOnChange = accessor.registerOnChange.bind(accessor);
    accessor.registerOnChange = (fn: (value: any) => void) => {
      originalRegisterOnChange((raw: any) => {
        const sanitized = this.sanitize(String(raw ?? ''));
        if (this.el.nativeElement.value !== sanitized) this.el.nativeElement.value = sanitized;
        fn(this.parse(sanitized));
      });
    };
  }

  ngOnInit() {
    this.format();
  }

  @HostListener('blur')
  format() {
    if (!this.padDecimals) return;
    const input = this.el.nativeElement;
    const num = this.parse(this.sanitize(input.value ?? ''));
    if (num !== null) input.value = num.toFixed(this.decimals);
  }

  // Keeps an optional leading "-", digits and a single "." (any "," becomes "."), trimmed to
  // `decimals` decimals. Range limits stay with the control's own validators (Validators.min...).
  private sanitize(value: string): string {
    const sign = value.trim().startsWith('-') ? '-' : '';
    const cleaned = value.replace(/,/g, '.').replace(/[^\d.]/g, '');
    const [int, ...rest] = cleaned.split('.');
    return sign + (rest.length ? `${int}.${rest.join('').slice(0, this.decimals)}` : int);
  }

  private parse(value: string): number | null {
    if (['', '.', '-', '-.'].includes(value)) return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  }
}
